import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
    List as ListIcon,
    ListChecks,
    MoreVertical,
    ChevronDown,
    Download,
    CheckSquare,
    Plus,
    KanbanSquare,
    LayoutList,
    Users,
    ListTodo,
    BookOpen,
    ExternalLink,
    LayoutTemplate,
    Inbox,
    Zap,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
    DndContext,
    closestCenter,
    PointerSensor,
    useSensor,
    useSensors,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { useTasks } from "../hooks/useTasks";
import { useTaskLists, useTaskEmployees } from "../hooks/useTaskLists";
import TaskQuickAdd from "./TaskQuickAdd";
import TaskListFilters from "./TaskListFilters";
import TaskRow from "./TaskRow";
import TaskDetailDrawer from "./TaskDetailDrawer";
import TaskListsSection from "./TaskListsSection";
import ListColorModal from "./ListColorModal";
import StaffTasksEmpty from "./StaffTasksEmpty";
import TaskViewTabs from "./TaskViewTabs";
import TaskAssignmentView from "./TaskAssignmentView";
import TaskAnalyticsPanel from "./TaskAnalyticsPanel";
import TaskBoard from "./TaskBoard";
import TaskKpiBar from "./TaskKpiBar";
import TaskMobileList from "./TaskMobileList";
import TaskStaffQueue from "./TaskStaffQueue";
import TaskTemplateLibrary from "./TaskTemplateLibrary";
import TaskAutomationLibrary from "./TaskAutomationLibrary";
import TaskTriageInbox from "./TaskTriageInbox";
import TaskSavedViewsBar from "./TaskSavedViewsBar";
import BulkAssignBar from "./BulkAssignBar";
import TaskCommandPalette, { useTaskCommandShortcut } from "./TaskCommandPalette";
import { groupTasksByAssignee } from "../utils/taskStats";
import { exportTasksToCsv } from "../utils/taskExport";
import { requestTaskNotificationPermission } from "../utils/taskNotifications";
import { ensureTaskNotificationPermission } from "../utils/taskReminderSync";
import TaskReminderSetupBanner, { MissedRemindersBar } from "./TaskReminderBanners";
import "../tasks-layout.css";

function useIsMobile() {
    const [isMobile, setIsMobile] = useState(
        () => typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches
    );
    useEffect(() => {
        const mq = window.matchMedia("(max-width: 767px)");
        const onChange = () => setIsMobile(mq.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);
    return isMobile;
}

const TasksPage = ({
    role,
    apiUrl,
    getToken,
    TaskModal,
    TaskModalEdit,
    TaskHistory,
    EmptyStateBlock,
    howToUseHref,
}) => {
    const token = getToken();
    const location = useLocation();
    const isStaff = role === "staff";
    const isAdmin = !isStaff;

    const [menuOpen, setMenuOpen] = useState(false);
    const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
    const [automationLibraryOpen, setAutomationLibraryOpen] = useState(false);
    const [pendingTemplate, setPendingTemplate] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
    const [taskEdit, setTaskEdit] = useState(null);
    const [viewMode, setViewMode] = useState(role === "staff" ? "now" : "board");
    const [inboxPin, setInboxPin] = useState("unassigned");
    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());
    const [paletteOpen, setPaletteOpen] = useState(false);
    const [setupBannerDismissed, setSetupBannerDismissed] = useState(false);
    const [composerOpen, setComposerOpen] = useState(false);
    const quickAddInputRef = useRef(null);
    const isMobile = useIsMobile();

    const taskState = useTasks({ role, apiUrl, getToken });
    const listState = useTaskLists({ role, apiUrl, getToken });
    const { assignOptions } = useTaskEmployees({ role, apiUrl, getToken });

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
    );

    const assignmentGroups = useMemo(
        () =>
            isAdmin
                ? groupTasksByAssignee(
                      taskState.tasks.filter((t) => t.status !== "Completed"),
                      assignOptions
                  )
                : [],
        [isAdmin, taskState.tasks, assignOptions]
    );

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get("openTask") === "true") {
            window.history.replaceState({}, "", window.location.pathname);
            setIsModalOpen(true);
        }

        const taskId = params.get("taskId");
        const action = params.get("action");
        const snooze = params.get("snooze");
        if (taskId) {
            window.history.replaceState({}, "", window.location.pathname);
            taskState.handleTaskReminderDeepLink?.({ taskId, action, snooze }).finally(() => {
                taskState.fetchTasks();
            });
            const match = taskState.tasks.find((t) => String(t.id) === String(taskId));
            if (match) taskState.setSelectedTask(match);
        }

        const filter = params.get("filter");
        if (filter && isAdmin) {
            if (filter === "today") {
                taskState.setDueFilter("today");
                taskState.setStatusFilter("active");
            } else if (filter === "overdue") {
                taskState.setDueFilter("overdue");
                taskState.setStatusFilter("active");
            } else if (filter === "active") {
                taskState.setDueFilter("active");
                taskState.setStatusFilter("active");
            } else if (filter === "unassigned" || filter === "high" || filter === "nodue") {
                setInboxPin(filter);
                setViewMode("inbox");
            } else if (filter === "inbox") {
                setViewMode("inbox");
            }
        }
    }, [location.search, isAdmin, taskState.handleTaskReminderDeepLink, taskState.fetchTasks, taskState.setSelectedTask, taskState.tasks]);

    useEffect(() => {
        requestTaskNotificationPermission();
        ensureTaskNotificationPermission();
    }, []);

    const openPalette = useCallback(() => setPaletteOpen(true), []);
    useTaskCommandShortcut(openPalette);

    const handleExportClick = useCallback(() => {
        exportTasksToCsv(taskState.filteredTasks);
        toast.success("Tasks exported to CSV");
        setMenuOpen(false);
    }, [taskState.filteredTasks]);

    const handleCommandAction = useCallback(
        (id, meta) => {
            switch (id) {
                case "focus-add":
                    quickAddInputRef.current?.focus();
                    break;
                case "advanced":
                    setIsModalOpen(true);
                    break;
                case "filter-today":
                    if (isStaff) {
                        taskState.setViewFilter("today");
                    } else {
                        taskState.setDueFilter("today");
                        taskState.setStatusFilter("active");
                    }
                    setViewMode("board");
                    break;
                case "filter-overdue":
                    taskState.setDueFilter("overdue");
                    taskState.setStatusFilter("active");
                    setViewMode("board");
                    break;
                case "filter-active":
                    if (isStaff) {
                        taskState.setViewFilter("all");
                    } else {
                        taskState.setDueFilter("active");
                        taskState.setStatusFilter("active");
                    }
                    setViewMode("board");
                    break;
                case "export":
                    handleExportClick();
                    break;
                case "view-board":
                    setViewMode("board");
                    break;
                case "view-now":
                    setViewMode("now");
                    break;
                case "view-list":
                    setViewMode("list");
                    break;
                case "view-assignments":
                    setViewMode("assignments");
                    break;
                case "view-analytics":
                    setViewMode("analytics");
                    break;
                case "view-inbox":
                    setViewMode("inbox");
                    break;
                case "open-automations":
                    setAutomationLibraryOpen(true);
                    break;
                case "inbox-unassigned":
                case "inbox-overdue":
                case "inbox-high":
                case "inbox-nodue":
                    setInboxPin(id.replace("inbox-", ""));
                    setViewMode("inbox");
                    break;
                case "bulk-assign":
                    setSelectionMode(true);
                    setSelectedIds(new Set());
                    setViewMode("list");
                    break;
                case "view-today":
                    taskState.setViewFilter("today");
                    break;
                case "view-all":
                    taskState.setViewFilter("all");
                    break;
                default:
                    if (id.startsWith("assign-")) {
                        taskState.setAssigneeFilter(
                            String(meta?.assignTo ?? id.replace("assign-", ""))
                        );
                        setViewMode("board");
                    }
            }
        },
        [isStaff, taskState, handleExportClick]
    );

    const handleEditTask = (task) => {
        setTaskEdit(task);
        setIsModalOpenEdit(true);
    };

    const toggleSelect = (id, checked) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(id);
            else next.delete(id);
            return next;
        });
    };

    const handleBulkAssign = (assignTo) => {
        taskState.bulkAssign([...selectedIds], assignTo);
        setSelectedIds(new Set());
        setSelectionMode(false);
    };

    const handleMenuAction = (action) => {
        if (action === "export") handleExportClick();
        if (action === "analytics") {
            setViewMode("analytics");
            setMenuOpen(false);
        }
        if (action === "inbox") {
            setViewMode("inbox");
            setMenuOpen(false);
        }
        if (action === "select") {
            setSelectionMode((p) => !p);
            setSelectedIds(new Set());
            setViewMode("list");
            setMenuOpen(false);
        }
    };

    const savedViewSnapshot = useMemo(
        () => ({
            statusFilter: taskState.statusFilter,
            priorityFilter: taskState.priorityFilter,
            dueFilter: taskState.dueFilter,
            assigneeFilter: taskState.assigneeFilter,
            sortBy: taskState.sortBy,
            viewFilter: taskState.viewFilter,
            viewMode,
            inboxPin,
        }),
        [
            taskState.statusFilter,
            taskState.priorityFilter,
            taskState.dueFilter,
            taskState.assigneeFilter,
            taskState.sortBy,
            taskState.viewFilter,
            viewMode,
            inboxPin,
        ]
    );

    const applySavedView = useCallback(
        (view) => {
            taskState.setStatusFilter(view.statusFilter || "");
            taskState.setPriorityFilter(view.priorityFilter || "");
            taskState.setDueFilter(view.dueFilter || "");
            taskState.setAssigneeFilter(view.assigneeFilter || "");
            taskState.setSortBy(view.sortBy || "");
            if (isStaff) {
                taskState.setViewFilter(view.viewFilter || "today");
            }
            if (view.inboxPin) setInboxPin(view.inboxPin);
            const nextMode = view.viewMode || (isStaff ? "now" : "board");
            if (isStaff && nextMode === "inbox") {
                setViewMode("now");
            } else {
                setViewMode(nextMode);
            }
        },
        [isStaff, taskState]
    );

    const handleInboxAssign = useCallback(
        (taskId, assignTo) => {
            taskState.updateTaskField(taskId, "assign_to", assignTo || null);
        },
        [taskState]
    );

    const activeHeading = isStaff
        ? taskState.viewFilter === "today"
            ? `Due today (${taskState.activeTasks.length})`
            : `My tasks (${taskState.activeTasks.length})`
        : taskState.assigneeFilter
          ? `Filtered tasks (${taskState.activeTasks.length})`
          : taskState.dueFilter === "today"
            ? `Due today (${taskState.activeTasks.length})`
            : taskState.dueFilter === "overdue"
              ? `Overdue (${taskState.activeTasks.length})`
              : taskState.dueFilter === "none"
                ? `No due date (${taskState.activeTasks.length})`
                : `Active tasks (${taskState.activeTasks.length})`;

    const quickAddProps = {
        role,
        apiUrl,
        token,
        assignOptions,
        onTaskCreated: taskState.fetchTasks,
        inputRef: quickAddInputRef,
        onAdvancedClick: () => {
            setComposerOpen(false);
            setIsModalOpen(true);
        },
    };

    const handleKpiSelect = (id) => {
        if (id === "open") {
            taskState.setStatusFilter("Open");
            taskState.setDueFilter("");
        } else if (id === "progress") {
            taskState.setStatusFilter("In Progress");
            taskState.setDueFilter("");
        } else if (id === "overdue") {
            taskState.setStatusFilter("active");
            taskState.setDueFilter("overdue");
        } else if (id === "completed-today") {
            taskState.setStatusFilter("Completed");
            taskState.setDueFilter("");
            taskState.setCompletedOpen(true);
        } else if (id === "workload" && isAdmin) {
            setViewMode("assignments");
        }
    };

    const hasDrawer = Boolean(taskState.selectedTask);
    const showMobileBar = isStaff || isAdmin;

    const renderTaskList = () => (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={taskState.handleDragEnd}
        >
            <SortableContext
                items={taskState.filteredTasks.map((t) => t.id)}
                strategy={verticalListSortingStrategy}
            >
                <div className="space-y-2">
                    <div className="flex items-center justify-between gap-2 mb-1">
                        <div className="text-sm font-semibold text-ink">{activeHeading}</div>
                        {isAdmin && selectionMode && (
                            <button
                                type="button"
                                onClick={() => {
                                    const all = taskState.activeTasks.map((t) => t.id);
                                    setSelectedIds(
                                        selectedIds.size === all.length
                                            ? new Set()
                                            : new Set(all)
                                    );
                                }}
                                className="text-xs text-primary font-medium hover:underline min-h-[44px] px-2"
                            >
                                {selectedIds.size === taskState.activeTasks.length
                                    ? "Deselect all"
                                    : "Select all"}
                            </button>
                        )}
                    </div>
                    {taskState.activeTasks.map((task) => (
                        <TaskRow
                            key={task.id}
                            task={task}
                            role={role}
                            onComplete={taskState.handleCompleteTask}
                            onEdit={handleEditTask}
                            onDelete={taskState.handleDeleteTask}
                            onView={taskState.setSelectedTask}
                            onTogglePause={taskState.handleTogglePause}
                            selectable={isAdmin && selectionMode}
                            selected={selectedIds.has(task.id)}
                            onSelectChange={toggleSelect}
                        />
                    ))}
                    {taskState.activeTasks.length === 0 &&
                        (isStaff ? (
                            <StaffTasksEmpty viewFilter={taskState.viewFilter} />
                        ) : EmptyStateBlock ? (
                            <EmptyStateBlock
                                compact
                                Icon={ListChecks}
                                title="No active tasks"
                                description="Create a task above or adjust your filters."
                                className="max-w-full !mt-0 border-dashed"
                            />
                        ) : (
                            <div className="rounded-lg border border-dashed border-line p-6 text-center text-ink-muted text-sm">
                                No active tasks. Add one above.
                            </div>
                        ))}

                    {taskState.completedTasks.length > 0 && (
                        <div className="mt-4">
                            <button
                                type="button"
                                onClick={() => taskState.setCompletedOpen((p) => !p)}
                                className="flex items-center gap-2 text-sm font-semibold text-ink-secondary hover:text-ink w-full min-h-[44px]"
                            >
                                <ChevronDown
                                    size={16}
                                    className={`transition-transform ${
                                        taskState.completedOpen ? "rotate-180" : ""
                                    }`}
                                />
                                Completed ({taskState.completedTasks.length})
                            </button>
                            <AnimatePresence>
                                {taskState.completedOpen && (
                                    <motion.div
                                        initial={{ opacity: 0, height: 0 }}
                                        animate={{ opacity: 1, height: "auto" }}
                                        exit={{ opacity: 0, height: 0 }}
                                        className="mt-2 space-y-2"
                                    >
                                        {taskState.completedTasks.map((task) => (
                                            <TaskRow
                                                key={task.id}
                                                task={task}
                                                role={role}
                                                onComplete={taskState.handleCompleteTask}
                                                onEdit={handleEditTask}
                                                onDelete={taskState.handleDeleteTask}
                                                onView={taskState.setSelectedTask}
                                                onTogglePause={taskState.handleTogglePause}
                                            />
                                        ))}
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    )}
                </div>
            </SortableContext>
        </DndContext>
    );

    return (
        <div className={`tasks-page ${showMobileBar ? "tasks-page--mobile-nav" : ""}`}>
            <header className="tasks-page__header">
                <div className="tasks-page__header-start">
                    <h1 className="dashboard-title">{isStaff ? "My Tasks" : "Tasks"}</h1>
                    <button
                        type="button"
                        onClick={openPalette}
                        className="tasks-page__cmdk"
                        title="Command palette (Ctrl+K)"
                    >
                        <span>⌘K</span>
                    </button>
                </div>

                <div className="tasks-page__header-center">
                    <TaskViewTabs
                        active={viewMode}
                        onChange={setViewMode}
                        role={role}
                        compact
                    />
                </div>

                <div className="tasks-page__actions relative">
                    <button
                        type="button"
                        onClick={() => listState.setListSectionOpen((p) => !p)}
                        className="oms-add-btn min-h-[44px]"
                    >
                        <span className="hidden sm:inline">List</span>
                        <ListIcon className="w-4 h-4" />
                    </button>
                    <button
                        type="button"
                        onClick={() => setTemplateLibraryOpen(true)}
                        className="oms-add-btn min-h-[44px]"
                        title="Task templates"
                    >
                        <span className="hidden sm:inline">Templates</span>
                        <LayoutTemplate className="w-4 h-4" />
                    </button>
                    {isAdmin && (
                        <button
                            type="button"
                            onClick={() => setAutomationLibraryOpen(true)}
                            className="oms-add-btn min-h-[44px]"
                            title="Task automations"
                        >
                            <span className="hidden sm:inline">Rules</span>
                            <Zap className="w-4 h-4" />
                        </button>
                    )}

                    {isAdmin && (
                        <>
                            <button
                                type="button"
                                onClick={() => setIsModalOpen(true)}
                                className="tasks-page__header-advanced oms-add-btn min-h-[44px] min-w-[44px]"
                                title="Create task"
                                aria-label="Create task"
                            >
                                <span>Create</span>
                                <Plus className="w-4 h-4" />
                            </button>
                            <button
                                type="button"
                                className="p-2 rounded-full hover:bg-surface-2 transition min-h-[44px] min-w-[44px] flex items-center justify-center"
                                onClick={() => setMenuOpen((p) => !p)}
                                aria-label="More task actions"
                            >
                                <MoreVertical className="w-5 h-5 text-ink-secondary" />
                            </button>
                            <AnimatePresence>
                                {menuOpen && (
                                    <>
                                        <div
                                            className="fixed inset-0 z-40"
                                            onClick={() => setMenuOpen(false)}
                                            aria-hidden
                                        />
                                        <motion.div
                                            initial={{ opacity: 0, y: -8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, y: -8 }}
                                            className="absolute right-0 top-full mt-2 w-52 bg-surface shadow-xl rounded-xl overflow-hidden z-50 border border-line"
                                        >
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("export")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <Download size={14} /> Export CSV
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("select")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <CheckSquare size={14} />
                                                {selectionMode ? "Cancel selection" : "Bulk assign"}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("inbox")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <Inbox size={14} />
                                                Inbox
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => handleMenuAction("analytics")}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                View analytics
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setTemplateLibraryOpen(true);
                                                    setMenuOpen(false);
                                                }}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <LayoutTemplate size={14} />
                                                Templates
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setAutomationLibraryOpen(true);
                                                    setMenuOpen(false);
                                                }}
                                                className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <Zap size={14} />
                                                Automations
                                            </button>
                                            {howToUseHref && (
                                                <a
                                                    href={howToUseHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={() => setMenuOpen(false)}
                                                    className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                                >
                                                    <BookOpen size={14} />
                                                    How to use
                                                    <ExternalLink size={12} className="opacity-70 ml-auto" />
                                                </a>
                                            )}
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setIsModalOpen(true);
                                                    setMenuOpen(false);
                                                }}
                                                className="flex md:hidden items-center gap-2 w-full text-left px-4 py-2.5 text-sm text-ink-secondary hover:bg-surface-2 min-h-[44px]"
                                            >
                                                <Plus size={14} />
                                                Create task
                                            </button>
                                        </motion.div>
                                    </>
                                )}
                            </AnimatePresence>
                        </>
                    )}
                </div>
            </header>

            <TaskListsSection
                isOpen={listState.listSectionOpen}
                lists={listState.lists}
                activeListId={listState.activeListId}
                setActiveListId={listState.setActiveListId}
                onCreateClick={() => {
                    listState.setEditList(null);
                    listState.setListName("");
                    listState.setSelectedColor(null);
                    listState.setPopupOpen(true);
                }}
                onEditClick={(list) => {
                    listState.setEditList(list);
                    listState.setPopupOpen(true);
                }}
                onDeleteList={listState.handleDeleteList}
            />

            <ListColorModal
                isOpen={listState.popupOpen}
                onClose={() => {
                    listState.setPopupOpen(false);
                    listState.setEditList(null);
                }}
                listName={listState.listName}
                setListName={listState.setListName}
                availableColors={listState.availableColors}
                selectedColor={listState.selectedColor}
                setSelectedColor={listState.setSelectedColor}
                onSubmit={listState.handleCreateList}
                isEdit={!!listState.editList}
            />

            <div className="tasks-page__workspace">
                <div
                    className={`tasks-page__grid ${hasDrawer ? "tasks-page__grid--split" : ""}`}
                >
                    <div className={`tasks-page__main text-xs md:text-[13px] ${viewMode === "board" ? "tasks-page__main--board" : ""}`}>
                        <TaskReminderSetupBanner
                            dismissed={setupBannerDismissed}
                            onDismiss={() => setSetupBannerDismissed(true)}
                        />
                        <MissedRemindersBar
                            tasks={taskState.missedReminders}
                            onOpenTask={(task) => taskState.setSelectedTask(task)}
                            onDismissTask={(task) => taskState.dismissMissedReminder(task.id)}
                        />
                        <div className="tasks-page__composer hidden md:block">
                            <TaskQuickAdd {...quickAddProps} variant="inline" />
                        </div>

                        <TaskSavedViewsBar
                            role={role}
                            snapshot={savedViewSnapshot}
                            onApply={applySavedView}
                            tasks={taskState.tasks}
                            inboxPin={inboxPin}
                            onOpenInbox={(pinId) => {
                                setInboxPin(pinId);
                                setViewMode("inbox");
                            }}
                        />

                        {(viewMode === "board" || viewMode === "list" || viewMode === "now") && (
                            <TaskListFilters
                                role={role}
                                searchQuery={taskState.searchQuery}
                                setSearchQuery={taskState.setSearchQuery}
                                statusFilter={taskState.statusFilter}
                                setStatusFilter={taskState.setStatusFilter}
                                priorityFilter={taskState.priorityFilter}
                                setPriorityFilter={taskState.setPriorityFilter}
                                assigneeFilter={taskState.assigneeFilter}
                                setAssigneeFilter={taskState.setAssigneeFilter}
                                dueFilter={taskState.dueFilter}
                                setDueFilter={taskState.setDueFilter}
                                sortBy={taskState.sortBy}
                                setSortBy={taskState.setSortBy}
                                viewFilter={taskState.viewFilter}
                                setViewFilter={taskState.setViewFilter}
                                todayCount={taskState.todayCount}
                                assignOptions={assignOptions}
                                onAdvancedClick={() => setIsModalOpen(true)}
                                onCompletedFilter={() => taskState.setCompletedOpen(true)}
                                hasActiveFilters={Boolean(
                                    taskState.dueFilter ||
                                        taskState.assigneeFilter ||
                                        taskState.statusFilter ||
                                        taskState.priorityFilter ||
                                        taskState.searchQuery
                                )}
                                onClearFilters={() => {
                                    taskState.setDueFilter("");
                                    taskState.setAssigneeFilter("");
                                    taskState.setStatusFilter("");
                                    taskState.setPriorityFilter("");
                                    taskState.setSearchQuery("");
                                }}
                            />
                        )}

                        {viewMode === "board" && !isMobile && (
                            <TaskKpiBar
                                tasks={taskState.tasks}
                                onSelect={handleKpiSelect}
                            />
                        )}

                        {viewMode === "now" && (
                            <TaskStaffQueue
                                tasks={taskState.filteredTasks}
                                onView={taskState.setSelectedTask}
                                onStart={taskState.handleStartTask}
                                onPause={taskState.handlePauseTask}
                                onSnooze={taskState.handleSnoozeTask}
                                onComplete={(task, file) =>
                                    taskState.handleCompleteTask(task.id, task.status, file)
                                }
                            />
                        )}

                        {viewMode === "board" && (
                            <>
                                {isStaff &&
                                    taskState.filteredTasks.length === 0 && (
                                        <div className="mb-3">
                                            <StaffTasksEmpty viewFilter={taskState.viewFilter} />
                                        </div>
                                    )}
                                {isMobile ? (
                                    <TaskMobileList
                                        tasks={taskState.filteredTasks}
                                        role={role}
                                        assignOptions={assignOptions}
                                        onView={taskState.setSelectedTask}
                                    />
                                ) : (
                                    <TaskBoard
                                        tasks={taskState.filteredTasks}
                                        role={role}
                                        assignOptions={assignOptions}
                                        sortBy={taskState.sortBy}
                                        onMove={taskState.moveTaskToColumn}
                                        onView={taskState.setSelectedTask}
                                    />
                                )}
                            </>
                        )}

                        {viewMode === "list" && (
                            <>
                                {renderTaskList()}
                                {isAdmin && (
                                    <BulkAssignBar
                                        selectedCount={selectedIds.size}
                                        assignOptions={assignOptions}
                                        onAssign={handleBulkAssign}
                                        onClear={() => {
                                            setSelectedIds(new Set());
                                            setSelectionMode(false);
                                        }}
                                    />
                                )}
                            </>
                        )}

                        {isAdmin && viewMode === "inbox" && (
                            <TaskTriageInbox
                                tasks={taskState.tasks}
                                pinId={inboxPin}
                                onPinChange={setInboxPin}
                                assignOptions={assignOptions}
                                onAssign={handleInboxAssign}
                                onView={taskState.setSelectedTask}
                            />
                        )}

                        {isAdmin && viewMode === "assignments" && (
                            <TaskAssignmentView
                                groups={assignmentGroups}
                                onSelectTask={taskState.setSelectedTask}
                                onSelectGroup={(assignTo) => {
                                    taskState.setAssigneeFilter(String(assignTo));
                                    setViewMode("board");
                                }}
                            />
                        )}

                        {isAdmin && viewMode === "analytics" && (
                            <TaskAnalyticsPanel tasks={taskState.tasks} />
                        )}
                    </div>

                    <AnimatePresence>
                        {taskState.selectedTask && (
                            <div className="tasks-page__aside">
                                <TaskDetailDrawer
                                    task={taskState.selectedTask}
                                    onClose={() => taskState.setSelectedTask(null)}
                                    onUpdateField={taskState.updateTaskField}
                                    onUpdateChecklist={taskState.updateChecklist}
                                    onUploadChecklistPhoto={taskState.uploadChecklistPhoto}
                                    onAddComment={taskState.addComment}
                                    onSaveAsTemplate={taskState.saveAsTemplate}
                                    onStart={taskState.handleStartTask}
                                    onPause={taskState.handlePauseTask}
                                    onSnooze={taskState.handleSnoozeTask}
                                    snoozing={taskState.snoozingId === taskState.selectedTask?.id}
                                    leadOptions={listState.leadOptions}
                                    assignOptions={assignOptions}
                                    apiUrl={apiUrl}
                                    role={role}
                                    TaskHistory={TaskHistory}
                                />
                            </div>
                        )}
                    </AnimatePresence>
                </div>
            </div>

            {showMobileBar && (
                <>
                    {composerOpen && (
                        <div className="task-mobile-composer md:hidden">
                            <TaskQuickAdd {...quickAddProps} variant="dock" />
                        </div>
                    )}
                    <button
                        type="button"
                        className="task-fab md:hidden"
                        onClick={() => {
                            setComposerOpen((v) => !v);
                            window.setTimeout(() => quickAddInputRef.current?.focus(), 50);
                        }}
                        aria-label="Add task"
                    >
                        <Plus size={22} />
                    </button>
                    <nav className="task-bottom-nav md:hidden" aria-label="Task views">
                        {isStaff && (
                            <button
                                type="button"
                                className={viewMode === "now" ? "is-active" : ""}
                                onClick={() => setViewMode("now")}
                            >
                                <ListTodo size={18} />
                                Now
                            </button>
                        )}
                        <button
                            type="button"
                            className={viewMode === "list" ? "is-active" : ""}
                            onClick={() => setViewMode("list")}
                        >
                            <LayoutList size={18} />
                            List
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                className={viewMode === "inbox" ? "is-active" : ""}
                                onClick={() => setViewMode("inbox")}
                            >
                                <Inbox size={18} />
                                Inbox
                            </button>
                        )}
                        <button
                            type="button"
                            className={viewMode === "board" ? "is-active" : ""}
                            onClick={() => setViewMode("board")}
                        >
                            <KanbanSquare size={18} />
                            {isStaff ? "Board" : "Tasks"}
                        </button>
                        {isAdmin && (
                            <button
                                type="button"
                                className={viewMode === "assignments" ? "is-active" : ""}
                                onClick={() => setViewMode("assignments")}
                            >
                                <Users size={18} />
                                Team
                            </button>
                        )}
                    </nav>
                </>
            )}

            {TaskModal && (
                <TaskModal
                    isOpen={isModalOpen}
                    setIsOpen={setIsModalOpen}
                    leadOptions={listState.leadOptions}
                    assignOptions={assignOptions}
                    fetchTask={taskState.fetchTasks}
                    applyTemplate={pendingTemplate}
                    onApplied={() => setPendingTemplate(null)}
                />
            )}
            <TaskTemplateLibrary
                isOpen={templateLibraryOpen}
                onClose={() => setTemplateLibraryOpen(false)}
                apiUrl={apiUrl}
                getToken={getToken}
                onUse={(tpl) => {
                    setPendingTemplate(tpl);
                    setIsModalOpen(true);
                }}
            />
            {isAdmin && (
                <TaskAutomationLibrary
                    isOpen={automationLibraryOpen}
                    onClose={() => setAutomationLibraryOpen(false)}
                    apiUrl={apiUrl}
                    getToken={getToken}
                    assignOptions={assignOptions}
                />
            )}
            {TaskModalEdit && (
                <TaskModalEdit
                    isOpen={isModalOpenEdit}
                    setIsOpen={setIsModalOpenEdit}
                    leadOptions={listState.leadOptions}
                    assignOptions={assignOptions}
                    taskEdit={taskEdit}
                    fetchTask={taskState.fetchTasks}
                />
            )}

            <TaskCommandPalette
                isOpen={paletteOpen}
                onClose={() => setPaletteOpen(false)}
                role={role}
                onAction={handleCommandAction}
                assignOptions={assignOptions}
            />
        </div>
    );
};

export default TasksPage;
